import { RADIUS_STEPS, type RadiusStep } from './designTokens';
import type { PendingEdit } from './pendingEdits';

/**
 * A founder's design system change, on its way back to the startup's design system.
 *
 * Writing the token into `src/index.css` only changes this prototype's files, and the next build
 * seeds those files from the spec - so the colour held for a turn or two and then went back.
 * The startup's design system is the source every generation reads
 * (`PATCH /projects/:id/design-system` -> `enrich_spec_from_design_system` in the AI API -> the
 * spec's theme -> Chef's `buildThemeSeedFiles`), so a colour written there is the one every
 * prototype is built with from then on.
 *
 * Pure: pending edits in, the patch body out. The request itself belongs to the app, which has
 * the project id and the API client.
 */

/** The design system's field for each stylesheet token the editor can write. */
const FIELD_BY_TOKEN: Record<string, string> = {
  primary: 'primaryColor',
  'primary-foreground': 'primaryForeground',
  secondary: 'secondaryColor',
  'secondary-foreground': 'secondaryForeground',
  background: 'appBg',
  card: 'surfaceColor',
  border: 'borderColor',
  'muted-foreground': 'mutedTextColor',
  destructive: 'errorColor',
  // `radius` is the one that is not a colour: the design system keeps it as a named step.
  radius: 'borderRadius',
};

/** `175.3 77.4% 26.1%` -> `#0f766e`, the shape the design system stores colours in. */
export function hslPartsToHex(parts: string): string | null {
  const m = /^\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%\s*$/.exec(parts);
  if (!m) return null;
  const h = Number(m[1]) / 360;
  const s = Number(m[2]) / 100;
  const l = Number(m[3]) / 100;
  if (![h, s, l].every((n) => Number.isFinite(n))) return null;

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number): number => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  const [r, g, b] = s === 0 ? [l, l, l] : [channel(h + 1 / 3), channel(h), channel(h - 1 / 3)];
  const byte = (n: number) =>
    Math.round(Math.min(Math.max(n, 0), 1) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${byte(r)}${byte(g)}${byte(b)}`;
}

/** The corner step a `--radius` value stands for, named as the design system names it. */
export function radiusStepOf(value: string): RadiusStep | null {
  const exact = RADIUS_STEPS.find((step) => step.value === value.trim());
  if (exact) return exact.step;
  const m = /^([\d.]+)(px|rem)?$/.exec(value.trim());
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n)) return null;
  const px = m[2] === 'px' ? n : n * 16;
  if (px >= 999) return 'full';
  if (px >= 7) return 'lg';
  if (px >= 4) return 'md';
  if (px >= 1) return 'sm';
  return 'none';
}

/**
 * What to send to `PATCH /projects/:id/design-system` for the design system changes in `edits`,
 * or null when there are none. Only the fields the founder changed are included, so the patch
 * never restates the rest of the design system.
 */
export function designSystemPatchFor(edits: readonly PendingEdit[]): Record<string, string> | null {
  const patch: Record<string, string> = {};
  for (const edit of edits) {
    if (edit.kind !== 'theme') continue;
    const field = FIELD_BY_TOKEN[edit.token];
    if (!field) continue;
    if (edit.token === 'radius') {
      const step = radiusStepOf(edit.value);
      if (step) patch[field] = step;
      continue;
    }
    const hex = hslPartsToHex(edit.value);
    if (hex) patch[field] = hex;
  }
  return Object.keys(patch).length > 0 ? patch : null;
}
